import type {
  ActionResult,
  CardOutcome,
  DetectResult,
  MapData,
  PlaceArchive,
  PlaceCandidate,
  PlaceProgress,
  PlaceSummary,
  Rules,
  SearchResult,
  User,
  VisitState,
} from "./types";

/** An API error carrying the status code, so callers can react to 401 vs 409. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isAuth() {
    return this.status === 401;
  }
  /** The place could not be identified -- worth offering a retry rather than an error. */
  get isUnresolvable() {
    return this.status === 422;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      // The session cookie is HttpOnly, so it must be sent explicitly on every call.
      credentials: "same-origin",
      ...init,
    });
  } catch {
    // v1 is online-only. Say so plainly rather than showing a blank screen.
    throw new ApiError(0, "Keine Verbindung. Conquest braucht zum Spielen ein Netz.");
  }

  if (response.status === 204) return undefined as T;

  const body = await response.text();
  let payload: unknown;
  try {
    payload = body ? JSON.parse(body) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const detail =
      payload && typeof payload === "object" && "detail" in payload
        ? String((payload as { detail: unknown }).detail)
        : `Anfrage fehlgeschlagen (${response.status})`;
    throw new ApiError(response.status, detail);
  }
  return payload as T;
}

function postJson<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
}

export const api = {
  me: () => request<{ user: User | null }>("/api/me"),

  login: (username: string, password: string) =>
    postJson<{ user: User; expires_at: string }>("/api/login", { username, password }),

  logout: () => postJson<{ ok: boolean }>("/api/logout", {}),

  rules: () => request<Rules>("/api/rules"),

  detect: (lat: number, lon: number) => postJson<DetectResult>("/api/detect", { lat, lon }),

  /** Manual fallback for when geolocation is unavailable or refused. */
  searchPlaces: (query: string) =>
    request<{ results: SearchResult[] }>(`/api/search?q=${encodeURIComponent(query)}`).then(
      (r) => r.results,
    ),

  startVisit: (candidate: PlaceCandidate) =>
    postJson<VisitState>("/api/visits", {
      osm_id: candidate.osm_id,
      name: candidate.name,
      place_type: candidate.place_type,
      country_code: candidate.country_code,
      lat: candidate.lat,
      lon: candidate.lon,
    }),

  currentVisit: () => request<{ visit: VisitState | null }>("/api/visits/current"),

  endVisit: (visitId: number) => postJson<VisitState>(`/api/visits/${visitId}/end`, {}),

  /**
   * Resolve a card, optionally with evidence.
   *
   * `outcome` is the verdict on a Herausforderung card, and only that category may
   * be sent "failure". It is always sent explicitly rather than left to the
   * server's default, so a lost bet can never be mistaken for a won one.
   *
   * Safe to retry: the API is idempotent by card instance id, so a resend after a
   * dropped connection returns the stored outcome instead of awarding twice.
   */
  completeCard: (
    cardId: number,
    evidence: { photos?: File[]; audio?: Blob | null; note?: string },
    outcome: CardOutcome = "success",
  ) => {
    const form = new FormData();
    for (const photo of evidence.photos ?? []) form.append("photo", photo, photo.name);
    if (evidence.audio) {
      const extension = extensionForAudio(evidence.audio.type);
      form.append("audio", evidence.audio, `recording.${extension}`);
    }
    if (evidence.note?.trim()) form.append("note", evidence.note.trim());
    form.append("outcome", outcome);
    return request<ActionResult>(`/api/cards/${cardId}/complete`, { method: "POST", body: form });
  },

  discardCard: (cardId: number) => postJson<ActionResult>(`/api/cards/${cardId}/discard`, {}),
  holdCard: (cardId: number) => postJson<ActionResult>(`/api/cards/${cardId}/hold`, {}),
  vetoCategory: (cardId: number) => postJson<ActionResult>(`/api/cards/${cardId}/veto-category`, {}),

  /** Confirm the pinned Besuch card. Idempotent; pressing it twice changes nothing. */
  confirmVisit: (placeId: number) =>
    postJson<{ place: PlaceProgress }>(`/api/places/${placeId}/visited`, {}),

  places: () => request<{ places: PlaceSummary[] }>("/api/places"),
  place: (placeId: number) => request<PlaceArchive>(`/api/places/${placeId}`),
  map: () => request<MapData>("/api/map"),
};

/**
 * Browsers disagree about MediaRecorder output: Safari produces audio/mp4, Chrome
 * and Firefox audio/webm. The backend accepts both but keys the stored extension
 * off the filename when the mime is unfamiliar, so send a matching one.
 */
function extensionForAudio(mime: string): string {
  const base = mime.split(";")[0].trim();
  switch (base) {
    case "audio/mp4":
      return "m4a";
    case "audio/mpeg":
      return "mp3";
    case "audio/ogg":
      return "ogg";
    case "audio/wav":
    case "audio/x-wav":
      return "wav";
    default:
      return "webm";
  }
}

/** Wraps navigator.geolocation in a promise, with messages worth showing a user. */
export function currentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Dieser Browser kann deinen Standort nicht melden. Gib den Ort stattdessen ein."));
      return;
    }
    // Every message names the way forward: typing the place always works, so a
    // location failure is never a dead end.
    navigator.geolocation.getCurrentPosition(resolve, (error) => {
      const messages: Record<number, string> = {
        1: "Die Standortfreigabe ist aus. Gib den Ort stattdessen ein oder erlaube den Standort in den Browser-Einstellungen.",
        2: "Dein Standort ist gerade nicht verfügbar — das passiert in Gebäuden und bei schlechtem Empfang. Gib den Ort stattdessen ein.",
        3: "Die Standortsuche hat zu lange gedauert. Versuch es noch einmal oder gib den Ort ein.",
      };
      reject(new Error(messages[error.code] ?? "Standort konnte nicht ermittelt werden. Gib den Ort stattdessen ein."));
    }, {
      enableHighAccuracy: true,
      timeout: 20000,
      // Accept a recent fix: re-acquiring GPS costs battery and time, and a
      // settlement does not change over a minute.
      maximumAge: 60000,
    });
  });
}
